import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  PLAN_LIMITS, 
  SubscriptionTier, 
  PlanLimits,
  canAccessFeature,
  isWithinLimit,
  getRemainingQuota,
  getUpgradeTiers,
  PRICING,
  formatPrice
} from '@/lib/pricing';

interface UsePlanLimitsReturn {
  tier: SubscriptionTier;
  limits: PlanLimits;
  isPro: boolean;
  isBusiness: boolean;
  isPaid: boolean;
  canAccess: (feature: keyof PlanLimits) => boolean;
  isWithinLimit: (limitType: 'transactions' | 'invoiceScans' | 'apiCallsPerDay', usage: number) => boolean;
  getRemainingQuota: (limitType: 'transactions' | 'invoiceScans' | 'apiCallsPerDay', usage: number) => number | 'unlimited';
  upgradeTiers: SubscriptionTier[];
  nextTier: SubscriptionTier | null;
  nextTierPrice: string;
}

export function usePlanLimits(): UsePlanLimitsReturn {
  const { profile } = useAuth();
  
  const tier = useMemo((): SubscriptionTier => {
    const subscription = profile?.subscription;
    if (subscription === 'business') return 'business';
    if (subscription === 'pro') return 'pro';
    if (subscription === 'starter') return 'starter';
    return 'free';
  }, [profile?.subscription]);

  const limits = useMemo(() => PLAN_LIMITS[tier], [tier]);

  const isPro = tier === 'pro' || tier === 'business';
  const isBusiness = tier === 'business';
  const isPaid = tier !== 'free';

  const upgradeTiers = useMemo(() => getUpgradeTiers(tier), [tier]);
  const nextTier = upgradeTiers.length > 0 ? upgradeTiers[0] : null;
  const nextTierPrice = nextTier ? formatPrice(PRICING[nextTier].monthly) : '';

  return {
    tier,
    limits,
    isPro,
    isBusiness,
    isPaid,
    canAccess: (feature: keyof PlanLimits) => canAccessFeature(tier, feature),
    isWithinLimit: (limitType, usage) => isWithinLimit(tier, limitType, usage),
    getRemainingQuota: (limitType, usage) => getRemainingQuota(tier, limitType, usage),
    upgradeTiers,
    nextTier,
    nextTierPrice,
  };
}
